import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from "react";
import { useProctoring } from "../../hooks/useProctoring";
import { useMediaPipe } from "../../hooks/useMediaPipe";
import { useVoiceProctoring } from "../../hooks/useVoiceProctoring";
import type { AudioFlagPayload } from "../../hooks/useVoiceProctoring";
import { usePersonDetection } from "../../hooks/usePersonDetection";
import { useCameraObstruction } from "../../hooks/useCameraObstruction";
import { exams } from "../../api/exams";
import { sendSnapshot } from "../../api/proctor";
import type { SnapshotResponse, ViolationType } from "../../api/types/proctor.types";
import { useCustomToast } from "../customtoast/CustomToastProvider";

export interface ExamProctorHandle {
  takeSnapshot: () => Promise<void>;
}

type WarningType =
  | "no_face"
  | "multiple_faces"
  | "dark_room"
  | "looking_away"
  | "multiple_people_detected"
  | "camera_obstructed"
  | "phone_coverage_detected"
  | "background_movement"
  | "multiple_speakers"
  | "background_voice"
  | "prolonged_silence"
  | "noise_spike"
  // Advanced person detection
  | "person_behind_candidate"
  | "person_at_side"
  | "person_facing_away"
  | "person_lingering"
  | "looking_at_other_person"
  | "rapid_movement_detected"
  | "unauthorized_person_detected"
  // Advanced camera obstruction
  | "camera_partially_covered"
  | "camera_fully_covered"
  | "phone_detected_near_face"
  | "camera_turned_away"
  | "dark_object_blocking"
  // Enhanced proctoring (MediaPipe + spoof + blink + camera block)
  | "person_in_background"
  | "multiple_people_with_faces"
  | "possible_spoof_flat_image"
  | "no_blink_detected"
  | "camera_blocked"
  | "camera_static_or_covered";

const warningTypeToViolation: Record<WarningType, ViolationType> = {
  no_face: "no_face",
  multiple_faces: "multiple_faces",
  dark_room: "dark_room",
  looking_away: "look_away",
  multiple_people_detected: "multiple_people_detected",
  camera_obstructed: "camera_obstructed",
  phone_coverage_detected: "phone_coverage_detected",
  background_movement: "background_movement",
  multiple_speakers: "multiple_speakers",
  background_voice: "background_voice",
  prolonged_silence: "prolonged_silence",
  noise_spike: "noise_spike",
  person_behind_candidate: "person_behind_candidate",
  person_at_side: "person_at_side",
  person_facing_away: "person_facing_away",
  person_lingering: "person_lingering",
  looking_at_other_person: "looking_at_other_person",
  rapid_movement_detected: "rapid_movement_detected",
  unauthorized_person_detected: "unauthorized_person_detected",
  camera_partially_covered: "camera_partially_covered",
  camera_fully_covered: "camera_fully_covered",
  phone_detected_near_face: "phone_detected_near_face",
  camera_turned_away: "camera_turned_away",
  dark_object_blocking: "dark_object_blocking",
  person_in_background: "person_in_background",
  multiple_people_with_faces: "multiple_people_with_faces",
  possible_spoof_flat_image: "possible_spoof_flat_image",
  no_blink_detected: "no_blink_detected",
  camera_blocked: "camera_blocked",
  camera_static_or_covered: "camera_static_or_covered",
};

interface Warning {
  type: WarningType;
  message: string;
  icon: string;
}

interface ExamProctorProps {
  user_id: string;
  user_exam_id: string;
  assessment_id: string;
  onFlag: (result: SnapshotResponse) => void;
}

/** How long a violation must persist before showing a warning (ms) */
const WARNING_DELAY_MS = 1000; // Reduced from 3000ms for immediate response
/** Minimum ms between repeated warnings of the same type */
const WARNING_COOLDOWN_MS = 30000; // Increased to reduce endpoint/log flooding
/** How long the warning banner stays visible (ms) */
const WARNING_DISPLAY_MS = 5000; // Reduced from 6000ms for faster UI updates
/** How often to check for violations (ms) - for real-time monitoring */
const VIOLATION_CHECK_INTERVAL_MS = 500; // Check every 500ms for immediate detection
/** Minimum interval before sending another camera snapshot to the endpoint (ms) */
const CAMERA_SNAPSHOT_COOLDOWN_MS = 1000;

const ExamProctor = forwardRef<ExamProctorHandle, ExamProctorProps>(({
  user_id,
  user_exam_id,
  assessment_id,
  onFlag,
}, ref) => {

  const savedBaseline = JSON.parse(
    sessionStorage.getItem(`audio_baseline_${user_exam_id}`) ?? '{}'
  );

  // 1. Memoize the onFlag callback to prevent hook dependency cycles
  const handleFlag = useCallback((payload: AudioFlagPayload) => {
    if (warningTypeToViolation[payload.type as WarningType]) {
      exams.logViolation({
        userExamId: user_exam_id,
        payload: { type: warningTypeToViolation[payload.type as WarningType] },
      }).catch(console.error);
    }
  }, [user_exam_id]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const { captureFrame, faceStatus } = useMediaPipe(videoRef);

  // 2. Pass config directly. useVoiceProctoring should internally handle stability or we should destructure carefully.
  const { audioStatus, startListening, stopListening } = useVoiceProctoring({
    user_id,
    user_exam_id,
    assessment_id,
    baselineRms: savedBaseline?.features?.rms ?? 0.05,
    baselineDb: savedBaseline?.features?.db ?? -26,
    baselineAudioUrl: savedBaseline?.baseline_url ?? '',
    onFlag: handleFlag,
  });
  const personDetection = usePersonDetection(videoRef, true);
  const cameraObstruction = useCameraObstruction(videoRef, true);

  const [activeWarning, setActiveWarning] = useState<Warning | null>(null);
  const [warningCount, setWarningCount] = useState(0);

  // Refs to store latest state values for stable interval processing
  const faceStatusRef = useRef(faceStatus);
  const audioStatusRef = useRef(audioStatus);
  const personDetectionRef = useRef(personDetection);
  const cameraObstructionRef = useRef(cameraObstruction);

  // Update refs on each state change
  useEffect(() => { faceStatusRef.current = faceStatus; }, [faceStatus]);
  useEffect(() => { audioStatusRef.current = audioStatus; }, [audioStatus]);
  useEffect(() => { personDetectionRef.current = personDetection; }, [personDetection]);
  useEffect(() => { cameraObstructionRef.current = cameraObstruction; }, [cameraObstruction]);
  const [showAudioNotice, setShowAudioNotice] = useState(false);
  const lastCameraSnapshotAtRef = useRef(0);
  const sendingCameraSnapshotRef = useRef(false);

  const customToast = useCustomToast();
  const lastWarningTimeRef = useRef<Record<string, number>>({});
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const violationStartRef = useRef<Record<string, number>>({});
  const violationCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(Date.now());
  const cameraViolationTypes = useRef<Set<WarningType>>(new Set([
    "no_face",
    "multiple_faces",
    "dark_room",
    "looking_away",
    "multiple_people_detected",
    "camera_obstructed",
    "background_movement",
    "camera_partially_covered",
    "camera_fully_covered",
    "phone_detected_near_face",
    "camera_turned_away",
    "dark_object_blocking",
    "person_in_background",
    "multiple_people_with_faces",
    "possible_spoof_flat_image",
    "no_blink_detected",
    "camera_blocked",
    "camera_static_or_covered",
    "unauthorized_person_detected",
    "person_behind_candidate",
    "person_at_side",
    "person_facing_away",
    "person_lingering",
    "looking_at_other_person",
    "rapid_movement_detected",
  ]));

  useEffect(() => {
    let stream: MediaStream | null = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Camera access denied for proctoring:", err);
      }
    })();
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // ── Audio proctoring setup ────────────────────────────────────────────────
  useEffect(() => {
    // Show notice before starting audio
    setShowAudioNotice(true);
    const timer = setTimeout(() => {
      setShowAudioNotice(false);
      startListening();
    }, 3000);

    return () => {
      clearTimeout(timer);
      stopListening();
    };
  }, [startListening, stopListening]);

  const { takeSnapshot } = useProctoring(videoRef, {
    user_id,
    user_exam_id,
    assessment_id,
    onResult: (result) => {
      if (!result.matched || result.no_face_detected) {
        onFlag(result);
      }
    },
  });

  useImperativeHandle(ref, () => ({ takeSnapshot }), [takeSnapshot]);

  // ── Show a specific warning and log the violation ────────────────────────
  const captureAndSendCameraSnapshot = useCallback(
    async (warningType: WarningType) => {
      const now = Date.now();
      if (
        sendingCameraSnapshotRef.current ||
        now - lastCameraSnapshotAtRef.current < CAMERA_SNAPSHOT_COOLDOWN_MS
      ) {
        return;
      }

      sendingCameraSnapshotRef.current = true;
      try {
        const capture = await captureFrame(false);
        if (!capture.blob) return;

        await sendSnapshot({
          user_id,
          user_exam_id,
          assessment_id,
          image: capture.blob,
          violation_type: warningTypeToViolation[warningType],
        });
        lastCameraSnapshotAtRef.current = Date.now();
      } catch (err) {
        console.error("Camera violation snapshot failed:", err);
      } finally {
        sendingCameraSnapshotRef.current = false;
      }
    },
    [captureFrame, user_id, user_exam_id, assessment_id]
  );

  const showWarning = useCallback((warning: Warning) => {
    const now = Date.now();
    const lastTime = lastWarningTimeRef.current[warning.type] || 0;
    if (now - lastTime < WARNING_COOLDOWN_MS) return;

    lastWarningTimeRef.current[warning.type] = now;

    // Violations are recorded silently; the candidate is warned from the
    // periodic proctoring-status check in CandidateAssessment instead

    setActiveWarning(warning);
    setWarningCount((c) => c + 1);

    if (cameraViolationTypes.current.has(warning.type)) {
      captureAndSendCameraSnapshot(warning.type);
    } else {
      if (warningTypeToViolation[warning.type]) {
        exams.logViolation({ userExamId: user_exam_id, payload: { type: warningTypeToViolation[warning.type] } }).catch((err: unknown) =>
          console.error("Failed to log violation:", err)
        );
      }
    }

    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    warningTimerRef.current = setTimeout(() => setActiveWarning(null), WARNING_DISPLAY_MS);
  }, [captureAndSendCameraSnapshot, user_exam_id]);

  // ── Continuous real-time violation monitoring ─────────────────────────────
  useEffect(() => {
    const checkViolation = (type: WarningType, isActive: boolean, warning: Warning) => {
      const now = Date.now();
      if (isActive) {
        if (!violationStartRef.current[type]) {
          violationStartRef.current[type] = now;
        } else if (now - violationStartRef.current[type] >= WARNING_DELAY_MS) {
          showWarning(warning);
          violationStartRef.current[type] = now; // reset so it can re-trigger after cooldown
        }
      } else {
        delete violationStartRef.current[type];
      }
    };

    const checkAllViolations = () => {
      // Grace period: Wait 30 seconds after mounting before showing any proctoring warnings
      if (Date.now() - startTimeRef.current < 30000) return;

      const fs = faceStatusRef.current;
      const as = audioStatusRef.current;
      const pd = personDetectionRef.current;
      const co = cameraObstructionRef.current;

      // No face detected - IMMEDIATE (no duration check for instant detection)
      checkViolation("no_face", fs.count === 0, {
        type: "no_face",
        message: "No face detected! Please stay in front of the camera. Leaving the frame may be flagged as malpractice and could reduce your score.",
        icon: "⚠️",
      });

      // Multiple faces detected
      checkViolation("multiple_faces", fs.count > 1, {
        type: "multiple_faces",
        message: `${fs.count} faces detected! Only the candidate should be visible. This has been recorded and may be flagged as malpractice.`,
        icon: "🚫",
      });

      // Dark room
      const isDark = fs.flags.includes("too_dark");
      checkViolation("dark_room", isDark, {
        type: "dark_room",
        message: "Your environment is too dark. Please improve your lighting. Poor visibility may affect your proctoring score.",
        icon: "🔅",
      });

      // Looking away
      checkViolation("looking_away", fs.lookAwayDuration > 5000 && fs.count === 1, {
        type: "looking_away",
        message: "Please remain visible in the camera frame. Extended time away from camera has been noted.",
        icon: "👀",
      });

      // Multiple people detected
      const multiPeople = fs.flags.includes("multiple_people_detected");
      checkViolation("multiple_people_detected", multiPeople, {
        type: "multiple_people_detected",
        message: "Multiple people detected in the frame or background! Only the candidate should be present. This has been recorded.",
        icon: "👥",
      });

      // Camera obstruction
      const isObstructed = fs.flags.includes("camera_obstructed");
      checkViolation("camera_obstructed", isObstructed, {
        type: "camera_obstructed",
        message: "Camera appears to be obstructed or covered! Please ensure your camera has a clear view. This may affect your proctoring score.",
        icon: "🚫",
      });

      // Background movement
      const bgMovement = fs.flags.includes("background_movement");
      checkViolation("background_movement", bgMovement, {
        type: "background_movement",
        message: "Significant movement detected in the background! Ensure you're in a quiet space with no one else present.",
        icon: "🏃",
      });

      // ── Audio violations ──────────────────────────────────────────────────
      const bgVoice = as.flags.includes("background_voice");
      checkViolation("background_voice", bgVoice, {
        type: "background_voice",
        message: "Background audio detected. Please ensure you're in a quiet environment when possible.",
        icon: "👤",
      });

      const noiseSpike = as.flags.includes("noise_spike");
      checkViolation("noise_spike", noiseSpike, {
        type: "noise_spike",
        message: "Audio spike detected. This has been logged for monitoring purposes.",
        icon: "🔊",
      });

      const prolongedSilence = as.flags.includes("prolonged_silence");
      checkViolation("prolonged_silence", prolongedSilence, {
        type: "prolonged_silence",
        message: "Extended silence detected. This is normal for many assessments and has been logged for monitoring purposes.",
        icon: "🔇",
      });

      // ── Advanced Person Detection ──────────────────────────────────────────
      const unauthorizedPerson = pd.totalPersons > 1;
      checkViolation("unauthorized_person_detected", unauthorizedPerson, {
        type: "unauthorized_person_detected",
        message: `${pd.totalPersons} people detected! Only the candidate should be present. All persons have been tracked and recorded.`,
        icon: "🚨",
      });

      const personBehind = pd.flags.includes("person_behind_candidate");
      checkViolation("person_behind_candidate", personBehind, {
        type: "person_behind_candidate",
        message: "Someone is detected behind you! This is a serious violation. Ensure you are completely alone during the exam.",
        icon: "👤",
      });

      const personAtSide = pd.flags.includes("person_at_side");
      checkViolation("person_at_side", personAtSide, {
        type: "person_at_side",
        message: "Someone is detected at your side! This could indicate assistance or proxy test-taking. This has been recorded.",
        icon: "⚠️",
      });

      const personFacingAway = pd.flags.includes("person_facing_away");
      checkViolation("person_facing_away", personFacingAway, {
        type: "person_facing_away",
        message: "A person facing away from the camera detected! Suspicious positioning has been recorded.",
        icon: "🔄",
      });

      const personLingering = pd.flags.includes("person_lingering");
      checkViolation("person_lingering", personLingering, {
        type: "person_lingering",
        message: "Someone has been present for an extended time! Only the candidate should remain in the testing area.",
        icon: "⏱️",
      });

      const lookingAtOther = pd.flags.includes("looking_at_other_person");
      checkViolation("looking_at_other_person", lookingAtOther, {
        type: "looking_at_other_person",
        message: "You appear to be looking at another person! Keep your eyes on the screen. This behavior has been recorded.",
        icon: "👁️",
      });

      const rapidMovement = pd.flags.includes("rapid_movement");
      checkViolation("rapid_movement_detected", rapidMovement, {
        type: "rapid_movement_detected",
        message: "Rapid movement detected! Someone is entering or exiting the frame. Maintain a stable testing environment.",
        icon: "🏃",
      });

      // ── Advanced Camera Obstruction ───────────────────────────────────────
      const partiallyCovered = co.obstructionType === "camera_partially_covered" && co.severity !== "none";
      checkViolation("camera_partially_covered", partiallyCovered, {
        type: "camera_partially_covered",
        message: "Camera appears partially covered! Please ensure nothing is blocking your camera. This has been recorded.",
        icon: "⚠️",
      });

      const fullyCovered = co.obstructionType === "camera_fully_covered";
      checkViolation("camera_fully_covered", fullyCovered, {
        type: "camera_fully_covered",
        message: "Camera is fully covered! This is a critical violation. Uncover your camera immediately or risk exam termination.",
        icon: "🚫",
      });

      const phoneNearFace = co.obstructionType === "phone_detected_near_face";
      checkViolation("phone_detected_near_face", phoneNearFace, {
        type: "phone_detected_near_face",
        message: "Phone or electronic device detected near your face! Remove all devices immediately. This is a serious violation.",
        icon: "📱",
      });

      const cameraTurned = co.obstructionType === "camera_turned_away" && co.severity === "critical";
      checkViolation("camera_turned_away", cameraTurned, {
        type: "camera_turned_away",
        message: "Camera has been turned away or repositioned! Keep your camera stable and facing you at all times.",
        icon: "🔄",
      });

      const darkObjectBlocking = co.obstructionType === "dark_object_blocking" && co.severity !== "warning";
      checkViolation("dark_object_blocking", darkObjectBlocking, {
        type: "dark_object_blocking",
        message: "Dark object detected blocking the camera! Remove any objects in front of the camera immediately.",
        icon: "🚧",
      });

      const personInBackground = fs.flags.includes("person_in_background");
      checkViolation("person_in_background", personInBackground, {
        type: "person_in_background",
        message: "Someone detected in the background! Ensure you are alone during the exam.",
        icon: "👤",
      });

      const multiplePeopleWithFaces = fs.flags.includes("multiple_people_with_faces");
      checkViolation("multiple_people_with_faces", multiplePeopleWithFaces, {
        type: "multiple_people_with_faces",
        message: "Multiple people detected with visible faces! You must be alone during the exam.",
        icon: "👥",
      });

      const possibleSpoof = fs.flags.includes("possible_spoof_flat_image");
      checkViolation("possible_spoof_flat_image", possibleSpoof, {
        type: "possible_spoof_flat_image",
        message: "Suspicious face detection! Ensure you are present in person and not using a photo or screen.",
        icon: "⚠️",
      });

      const noBlink = fs.flags.includes("no_blink_detected");
      checkViolation("no_blink_detected", noBlink, {
        type: "no_blink_detected",
        message: "Unusual eye movement pattern detected. (Glasses may affect this). Otherwise, blink naturally to confirm presence.",
        icon: "👁️",
      });

      const cameraBlocked = fs.flags.includes("camera_blocked");
      checkViolation("camera_blocked", cameraBlocked, {
        type: "camera_blocked",
        message: "Camera appears to be blocked or turned off! Ensure your camera is active, unobstructed, and clear.",
        icon: "🚫",
      });

      const cameraStatic = fs.flags.includes("camera_static_or_covered");
      checkViolation("camera_static_or_covered", cameraStatic, {
        type: "camera_static_or_covered",
        message: "Camera feed is frozen or covered! Check your camera connection and ensure it's working properly.",
        icon: "❄️",
      });
    };

    // Run checks immediately
    checkAllViolations();

    // Set up continuous monitoring at VIOLATION_CHECK_INTERVAL_MS (500ms)
    violationCheckIntervalRef.current = setInterval(checkAllViolations, VIOLATION_CHECK_INTERVAL_MS);

    return () => {
      if (violationCheckIntervalRef.current) {
        clearInterval(violationCheckIntervalRef.current);
      }
    };
  }, [showWarning]);

  // ── Border color based on current status ──────────────────────────────────
  const borderColor =
    cameraObstruction.obstructionType === "camera_fully_covered" || cameraObstruction.severity === "critical"
      ? "rgba(153,27,27,1)"       // darkest red — camera fully covered (CRITICAL)
      : personDetection.totalPersons > 1
        ? "rgba(220,38,38,0.9)"   // deep red — multiple persons (highest priority)
        : cameraObstruction.obstructionType === "phone_detected_near_face"
          ? "rgba(220,38,38,0.85)" // deep red — phone near face
          : faceStatus.count === 0
            ? "rgba(239,68,68,0.8)"      // red — no face
            : faceStatus.count > 1
              ? "rgba(245,158,11,0.8)"    // amber — multiple faces
              : cameraObstruction.isObstructed && cameraObstruction.severity === "flag"
                ? "rgba(220,38,38,0.75)"  // red — camera obstruction flagged
                : faceStatus.flags.includes("camera_obstructed") || faceStatus.flags.includes("phone_coverage_detected")
                  ? "rgba(220,38,38,0.8)"   // deep red — camera issues
                  : personDetection.flags.includes("person_behind_candidate") || personDetection.flags.includes("looking_at_other_person")
                    ? "rgba(239,68,68,0.7)"  // red — person-related violations
                    : faceStatus.flags.includes("multiple_people_detected") || faceStatus.flags.includes("background_movement")
                      ? "rgba(249,115,22,0.8)" // orange — background issues
                      : cameraObstruction.isObstructed && cameraObstruction.severity === "warning"
                        ? "rgba(245,158,11,0.7)" // amber — camera obstruction warning
                        : faceStatus.flags.includes("too_dark")
                          ? "rgba(245,158,11,0.8)"  // amber — dark
                          : personDetection.totalPersons === 0
                            ? "rgba(245,158,11,0.6)"  // amber — no person detected
                            : "rgba(34,197,94,0.6)";  // green — all good

  return (
    <>
      {/* Camera pip */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "fixed",
          bottom: 10,
          left: 80,
          width: 40,
          height: 40,
          borderRadius: 8,
          objectFit: "cover",
          border: `2px solid ${borderColor}`,
          zIndex: 9999,
          background: "#000",
        }}
      />

      {/* Warning count badge - HIDDEN */}
      {/* {warningCount > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 74,
            left: 80,
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: "#ef4444",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            border: "2px solid #fff",
          }}
        >
          {warningCount > 9 ? "9+" : warningCount}
        </div>
      )} */}

      {/* Legacy warning banner removed (now using toasts) */}


      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Audio monitoring notice */}
      {showAudioNotice && (
        <div
          style={{
            position: "fixed",
            top: 80,
            right: 16,
            width: 300,
            zIndex: 10002,
            background: "linear-gradient(135deg, #87aa4b, #87aa4b)",
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            color: "#fff",
            animation: "slideInRight 0.3s ease-out",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>🎤</span>
            <div style={{ flex: 1, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Audio Proctoring Active</div>
              <p style={{ margin: 0, opacity: 0.9, fontSize: 12 }}>
                Your audio is being monitored to ensure exam integrity. Please ensure you're in a quiet space alone.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Audio status indicator - HIDDEN */}
      {/* {audioStatus.isListening && (
        <div
          style={{
            position: "fixed",
            bottom: 10,
            right: 170,
            width: 80,
            height: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            background: audioStatus.flags.length > 0 
              ? "rgba(239,68,68,0.15)"
              : "rgba(34,197,94,0.15)",
            border: `2px solid ${
              audioStatus.flags.includes("multiple_speakers") || audioStatus.flags.includes("background_voice")
                ? "rgba(220,38,38,0.8)"
                : audioStatus.flags.includes("noise_spike")
                  ? "rgba(245,158,11,0.8)"
                  : "rgba(34,197,94,0.6)"
            }`,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              fontSize: 24,
              animation: audioStatus.voiceDetected ? "pulse 1s infinite" : "none",
            }}
          >
            🎤
          </div>
          {audioStatus.speakerCountEstimate > 1 && (
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#dc2626",
                marginTop: 2,
              }}
            >
              {audioStatus.speakerCountEstimate} VOICES
            </div>
          )}
          {audioStatus.speakerCountEstimate <= 1 && (
            <div
              style={{
                width: "60%",
                height: 4,
                background: "rgba(0,0,0,0.2)",
                borderRadius: 2,
                marginTop: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${audioStatus.currentVolume}%`,
                  height: "100%",
                  background: audioStatus.voiceDetected ? "#22c55e" : "#6b7280",
                  transition: "width 0.1s ease-out",
                }}
              />
            </div>
          )}
        </div>
      )} */}

      {/* Person detection indicator - HIDDEN */}
      {/* {personDetection.ready && (
        <div
          style={{
            position: "fixed",
            bottom: 10,
            right: 260,
            width: 80,
            height: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            background: personDetection.totalPersons > 1 
              ? "rgba(239,68,68,0.2)"
              : personDetection.totalPersons === 1
                ? "rgba(34,197,94,0.15)"
                : "rgba(156,163,175,0.15)",
            border: `2px solid ${
              personDetection.totalPersons > 1
                ? "rgba(220,38,38,0.9)"
                : personDetection.totalPersons === 1
                  ? "rgba(34,197,94,0.6)"
                  : "rgba(156,163,175,0.6)"
            }`,
            zIndex: 9999,
          }}
        >
          <div style={{ fontSize: 24 }}>
            {personDetection.totalPersons > 1 ? "👥" : personDetection.totalPersons === 1 ? "👤" : "❓"}
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: personDetection.totalPersons > 1 
                ? "#dc2626" 
                : personDetection.totalPersons === 1 
                  ? "#22c55e" 
                  : "#6b7280",
              marginTop: 2,
            }}
          >
            {personDetection.totalPersons === 0 && "NO PERSON"}
            {personDetection.totalPersons === 1 && "1 PERSON"}
            {personDetection.totalPersons > 1 && `${personDetection.totalPersons} PEOPLE`}
          </div>
          {personDetection.stats.totalPersonsDetected > 1 && (
            <div
              style={{
                fontSize: 8,
                color: "#d97706",
                marginTop: 1,
              }}
            >
              ({personDetection.stats.totalPersonsDetected} tracked)
            </div>
          )}
        </div>
      )} */}

      {/* Camera obstruction indicator - HIDDEN */}
      {/* <div
        style={{
          position: "fixed",
          bottom: 10,
          right: 350,
          width: 80,
          height: 80,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          background: cameraObstruction.severity === "critical"
            ? "rgba(153,27,27,0.25)"
            : cameraObstruction.severity === "flag"
              ? "rgba(239,68,68,0.2)"
              : cameraObstruction.severity === "warning"
                ? "rgba(245,158,11,0.15)"
                : "rgba(34,197,94,0.15)",
          border: `2px solid ${
            cameraObstruction.severity === "critical"
              ? "rgba(153,27,27,1)"
              : cameraObstruction.severity === "flag"
                ? "rgba(220,38,38,0.9)"
                : cameraObstruction.severity === "warning"
                  ? "rgba(245,158,11,0.8)"
                  : "rgba(34,197,94,0.6)"
          }`,
          zIndex: 9999,
        }}
      >
        <div 
          style={{ 
            fontSize: 24,
            animation: cameraObstruction.isObstructed ? "pulse 1.5s infinite" : "none",
          }}
        >
          {cameraObstruction.obstructionType === "camera_fully_covered" && "🚫"}
          {cameraObstruction.obstructionType === "camera_partially_covered" && "⚠️"}
          {cameraObstruction.obstructionType === "phone_detected_near_face" && "📱"}
          {cameraObstruction.obstructionType === "camera_turned_away" && "🔄"}
          {cameraObstruction.obstructionType === "dark_object_blocking" && "🚧"}
          {cameraObstruction.obstructionType === "none" && "📹"}
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: cameraObstruction.severity === "critical" || cameraObstruction.severity === "flag"
              ? "#dc2626"
              : cameraObstruction.severity === "warning"
                ? "#d97706"
                : "#22c55e",
            marginTop: 2,
            textAlign: "center",
          }}
        >
          {cameraObstruction.severity === "critical" && "CRITICAL"}
          {cameraObstruction.severity === "flag" && "FLAGGED"}
          {cameraObstruction.severity === "warning" && "WARNING"}
          {cameraObstruction.severity === "none" && "CAM OK"}
        </div>
        {cameraObstruction.isObstructed && cameraObstruction.duration > 0 && (
          <div
            style={{
              fontSize: 8,
              color: "#6b7280",
              marginTop: 1,
            }}
          >
            {(cameraObstruction.duration / 1000).toFixed(1)}s
          </div>
        )}
        {cameraObstruction.severity === "none" && (
          <div
            style={{
              width: "60%",
              height: 3,
              background: "rgba(0,0,0,0.2)",
              borderRadius: 2,
              marginTop: 3,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${(cameraObstruction.metrics.imageEntropy / 8) * 100}%`,
                height: "100%",
                background: "#22c55e",
                transition: "width 0.3s ease-out",
              }}
            />
          </div>
        )}
      </div> */}
    </>
  );
});

export default ExamProctor;
